/**
 * channelNotAllowedModal.tsx
 */
import ButtonContainer from '@components/atoms/container/notAllowedButtonContainer';
import ChannelDown from '@components/elements/images/channelDown';
import ChannelLogo from '@components/atoms/images/channelLogo';
import ChannelUp from '@components/elements/images/channelUp';
import Focus from '@shared/types/enums/focus';
import LogoContainer from '@components/atoms/container/notAllowedChannelLogoContainer';
import ModalButton from '@components/elements/buttons/ui/modalButton';
import ModalContainer from '@components/atoms/container/modalContainer';
import Text from '@components/atoms/text/notAllowedChannelText';
import TextContainer from '@components/atoms/container/notAllowedChannelTextContainer';
import TranslationManager from '@shared/assets/locale/translationsManager';
import addPlanRequest from '@shared/api/addPlan/request';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { channelDown, channelUp } from '@shared/store/thunks/channels.thunk';
import { channelsSelector } from '@shared/store/slices/channels.slice';
import { popFocus, pushFocus } from '@shared/store/slices/focus.slice';
import { popRoute } from '@shared/store/thunks/route.thunk';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useEffect, useRef, useState } from 'react';

/**
 * Displays the channel not allowed modal.
 */
export default function ChannelNotAllowedModal() {
  const dispatch = useAppDispatch();
  const { currentChannel } = useAppSelector(channelsSelector);
  const [accepted, setAccepted] = useState(false);

  const channelCodeRef = useRef(currentChannel?.code);
  channelCodeRef.current = currentChannel?.code;

  const confirmButtonAction = () => {
    setAccepted(true);
    addPlanRequest({ channelCode: channelCodeRef.current });
  };

  useEffect(() => {
    dispatch(pushFocus(Focus.MODAL_BUTTONS));
    return () => {
      dispatch(popFocus());
    };
  }, []);

  useKeyboard({
    canBeFocused: false,
    keyDownHandler: (key) => {
      switch (key) {
        case 'UP':
        case 'CHANNEL_UP':
          dispatch(channelUp());
          break;
        case 'DOWN':
        case 'CHANNEL_DOWN':
          dispatch(channelDown());
          break;
        case 'SELECT':
          setAccepted(true);
          addPlanRequest({ channelCode: channelCodeRef.current });
          break;
        case 'BACK':
          dispatch(popRoute());
      }
    },
  });

  return (
    <ModalContainer>
      <ChannelUp />
      <LogoContainer>
        <ChannelLogo src={currentChannel?.logo} />
      </LogoContainer>
      <TextContainer>
        <Text>
          {TranslationManager.get(
            accepted
              ? 'MODAL.CHANNEL_NOT_ALLOWED.THANKS'
              : 'MODAL.CHANNEL_NOT_ALLOWED.MESSAGE_TOP'
          )}
        </Text>
        <Text>
          {TranslationManager.get(
            accepted
              ? 'MODAL.CHANNEL_NOT_ALLOWED.CONFIRM_MESSAGE'
              : 'MODAL.CHANNEL_NOT_ALLOWED.MESSAGE_BOTTOM'
          )}
        </Text>
      </TextContainer>
      {!accepted && (
        <ButtonContainer>
          <ModalButton
            action={confirmButtonAction}
            canClick
            focus={Focus.MODAL_BUTTONS}
            text={TranslationManager.get('MODAL.CHANNEL_NOT_ALLOWED.CONFIRM')}
          />
        </ButtonContainer>
      )}
      <ChannelDown />
    </ModalContainer>
  );
}
